/**
 * Search-result navigation helpers.
 *
 * Backend global search emits leading-slash list routes (`/foreman`, `/projects`).
 * The consumer builds the id-aware path first and only falls back to that list.
 */

export const RESULT_GROUP_DISPLAY_LIMIT = 3;

export const DEEP_LINK_NOTICE = {
  DOCUMENT_NO_JOBSITE: 'document-no-jobsite',
  TASK_NO_JOBSITE: 'task-no-jobsite',
  PERMIT_NO_JOBSITE: 'permit-no-jobsite',
  PERMIT_NO_SITE_CONTEXT: 'permit-no-site-context',
};

export function flattenVisibleSearchResults(results) {
  if (!results?.groups?.length) return [];
  return results.groups.flatMap((group) => {
    if (group.status === 'error' || !Array.isArray(group.results) || group.results.length === 0) {
      return [];
    }
    return group.results.slice(0, RESULT_GROUP_DISPLAY_LIMIT);
  });
}

function withQuery(pathname, params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') {
      search.set(key, String(value));
    }
  });
  const query = search.toString();
  return query ? `${pathname}?${query}` : pathname;
}

function fallbackPath(destination, fallback) {
  return destination && destination.startsWith('/') ? destination : fallback;
}

/**
 * Jobsite ObjectId for document / task / permit.
 *
 * For those types, `metadata.jobsiteId` is a Jobsite ObjectId string.
 * Do NOT fall back to `metadata.projectId` — that is a Project._id
 * (`WorkPermit.projectId` ref 'Project'), a different collection.
 *
 * For the `jobsite` result type, `metadata.jobsiteId` is the human code
 * (`Jobsite.jobsiteId`, e.g. `jobsite_1723…`). The resolver uses `payload.id`
 * (Jobsite._id) for that type and ignores the human code.
 */
function jobsiteObjectId(metadata = {}) {
  return metadata.jobsiteId || '';
}

/**
 * @returns {string|null} id-aware path, or null when payload is not a search result
 */
export function resolveSearchResultDestination(destination, payload = {}) {
  const resultType = payload.resultType;
  if (!resultType) return null;

  const id = payload.id;
  const metadata = payload.metadata || {};
  const jobsiteId = jobsiteObjectId(metadata);

  switch (resultType) {
    case 'worker': {
      const base = destination === '/foreman' ? '/foreman' : '/workers';
      return id ? withQuery(base, { worker: id }) : fallbackPath(destination, '/workers');
    }
    case 'jobsite':
      return id ? withQuery('/projects', { jobsite: id }) : '/projects';
    case 'document':
      return jobsiteId
        ? withQuery('/projects', { jobsite: jobsiteId, tab: 'documents' })
        : withQuery('/projects', { deepLinkNotice: DEEP_LINK_NOTICE.DOCUMENT_NO_JOBSITE });
    case 'task':
      return jobsiteId
        ? withQuery('/projects', { jobsite: jobsiteId, tab: 'taskboard' })
        : withQuery(fallbackPath(destination, '/foreman'), {
            deepLinkNotice: DEEP_LINK_NOTICE.TASK_NO_JOBSITE,
          });
    case 'permit':
      if (jobsiteId) {
        return withQuery('/projects', { jobsite: jobsiteId, tab: 'permits', permitId: id });
      }
      if (metadata.projectId) {
        return withQuery('/projects', {
          project: metadata.projectId,
          tab: 'permits',
          permitId: id,
        });
      }
      if (id) {
        return withQuery('/projects', { tab: 'permits', permitId: id });
      }
      return withQuery(fallbackPath(destination, '/foreman'), {
        deepLinkNotice: DEEP_LINK_NOTICE.PERMIT_NO_SITE_CONTEXT,
      });
    case 'training':
      return id ? withQuery('/training', { module: id }) : '/training';
    case 'report':
      return id ? withQuery('/reports', { report: id }) : '/reports';
    default:
      return null;
  }
}
